import { ReactNode } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Switch, Tooltip, Typography } from "antd";
import { NamePath } from "antd/lib/form/interface";
import { clsx } from "clsx";

import { RVFormItem } from "../../atoms/RVFormItem/RVFormItem";
import { RVIcon, RVIconSizeOptions } from "../../atoms/RVIcon/RVIcon";
import { RVColumn, RVRow } from "../../layouts/RVFlexbox/RVFlexbox";

import styles from "./RVFormToggle.module.scss";

type BaseRVFormToggleProps = {
    /** Form field name. Supports nested paths using arrays (e.g., ['settings', 'enabled']) */
    name: NamePath;
    /** Optional description text displayed below the toggle */
    description?: ReactNode;
    /** Optional tooltip text displayed on info icon hover */
    tooltip?: string;
    /** Optional custom icon to render next to the label as the tooltip trigger. Defaults to InfoCircleOutlined */
    tooltipIcon?: ReactNode;
    /** Whether the toggle is disabled */
    disabled?: boolean;
    /** Optional callback fired when toggle value changes */
    onChange?: (checked: boolean) => void;
    /** Whether the toggle is in a full-page form layout (increases bottom spacing from 16px to 40px) */
    isFullPageForm?: boolean;
} & (
    | {
          /**
           * Value transformer — converts the Switch's boolean to the stored form value.
           * Must be paired with getValueProps when the stored value is non-boolean;
           * without it, truthy non-boolean values (e.g. "static") would leave the Switch
           * permanently "on" since the stored value is passed to the `checked` prop.
           */
          getValueFromEvent: (checked: boolean) => unknown;
          /**
           * Prop transformer — converts the stored form value back to { checked: boolean } for the Switch.
           * Required when getValueFromEvent is provided.
           */
          getValueProps: (value: unknown) => { checked: boolean };
      }
    | { getValueFromEvent?: never; getValueProps?: never }
);

type WithLabel = BaseRVFormToggleProps & {
    /** Label text for the toggle */
    label: string;
    aiVariant?: never;
};

type WithAIVariant = BaseRVFormToggleProps & {
    label?: never;
    /** AI variant configuration. Displays "REGAL AI" branding with sparkle icon */
    aiVariant: {
        /** Text displayed before "REGAL AI" */
        prefix: string;
        /** Text displayed after "REGAL AI" */
        suffix: string;
    };
};

export type RVFormToggleProps = (WithLabel | WithAIVariant) & {
    /** Optional CSS class name for the toggle container */
    className?: string;
};

/**
 * RVFormToggle
 *
 * A molecule component that provides a toggle/switch form control integrated with Ant Design Form.
 * Supports both standard toggles and AI-branded variants with optional descriptions and tooltips.
 *
 * - Automatically sets valuePropName="checked" for proper form binding
 * - Supports nested form field names via NamePath (arrays)
 * - Optional AI branding with sparkle icon via aiVariant prop
 * - Optional description text below the toggle
 * - Optional info icon with tooltip
 * - Disabled state support
 * - Custom onChange handler support
 *
 * @example
 * // Basic usage
 * <Form>
 *   <RVFormToggle
 *     name="isEnabled"
 *     label="Enable Feature"
 *   />
 * </Form>
 *
 * @example
 * // With description and tooltip
 * <RVFormToggle
 *   name="skipGoodbyeMessage"
 *   label="Skip Goodbye Message"
 *   description="When enabled, skips the goodbye message and ends the call immediately"
 *   tooltip="This setting affects call flow behavior"
 *   disabled={isViewOnly}
 * />
 *
 * @example
 * // With custom tooltip icon
 * <RVFormToggle
 *   name="skipGoodbyeMessage"
 *   label="Skip Goodbye Message"
 *   tooltip="This setting affects call flow behavior"
 *   tooltipIcon={<QuestionCircleOutlined />}
 * />
 *
 * @example
 * // AI variant with branding
 * <RVFormToggle
 *   name="hasAiAssistEnabled"
 *   aiVariant={{ prefix: "Enable", suffix: "Assist" }}
 *   tooltip="Use AI to help trigger this moment during conversations"
 * />
 *
 * @example
 * // With nested field name
 * <RVFormToggle
 *   name={['voice', 'settings', 'override']}
 *   label="Voice Override"
 *   onChange={(checked) => console.log('Voice override:', checked)}
 * />
 *
 * @param {RVFormToggleProps} props - Component props
 * @returns {JSX.Element}
 */
export function RVFormToggle({
    name,
    label,
    description,
    tooltip,
    tooltipIcon,
    disabled = false,
    className,
    onChange,
    getValueFromEvent,
    getValueProps,
    isFullPageForm = false,
    aiVariant,
}: RVFormToggleProps): JSX.Element {
    const normalizedName = `${Array.isArray(name) ? name.join(".") : name}-toggle`;
    return (
        <RVColumn
            gap={2}
            className={clsx(styles.toggleContainer, { [styles.largeFormSpacing]: isFullPageForm }, className)}
        >
            <RVRow className={styles.inlineFormSwitch}>
                <RVRow gap={1} alignItems="center">
                    {aiVariant ? (
                        <>
                            <Typography.Text className={styles.toggleDescription}>{aiVariant.prefix}</Typography.Text>
                            <RVIcon icon={"sparkle"} size={RVIconSizeOptions.SMALL} />
                            <Typography.Text className={styles.toggleDescriptionBold}>REGAL AI</Typography.Text>
                            <Typography.Text className={styles.toggleDescription}>{aiVariant.suffix}</Typography.Text>
                        </>
                    ) : (
                        <Typography.Text className={styles.toggleDescriptionBold}>{label}</Typography.Text>
                    )}
                    {tooltip && (
                        <Tooltip title={tooltip}>
                            {tooltipIcon ?? <InfoCircleOutlined className={styles.info} />}
                        </Tooltip>
                    )}
                </RVRow>
                <RVFormItem
                    name={name}
                    valuePropName="checked"
                    noStyle
                    getValueFromEvent={getValueFromEvent}
                    getValueProps={getValueProps}
                >
                    <Switch data-testid={normalizedName} key={normalizedName} onChange={onChange} disabled={disabled} />
                </RVFormItem>
            </RVRow>
            {description && <Typography.Text className={styles.description}>{description}</Typography.Text>}
        </RVColumn>
    );
}
